import React from 'react';

import { Accordion, HeroLayout, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { parseHtml } from '@change-corgi/core-react/html';
import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import { ImageWithAttribution } from './components/ImageWithAttribution';
import { ShareGroup } from './components/ShareGroup';
import { usePageHeading } from './hooks/usePageHeading';

export const PageHeading: React.FC = () => {
	const { translate } = useI18n();
	const {
		data: {
			description,
			objective,
			hasMovementPhoto,
			petitionCount,
			petitionCountLocalized,
			supporterCount,
			supporterCountLocalized,
		},
	} = usePageHeading();

	return (
		<div className={cn('md:mt-16', { 'mt-4': !hasMovementPhoto })}>
			<HeroLayout
				as="section"
				media={hasMovementPhoto && <ImageWithAttribution imgAlt={objective} />}
				reverseMd
				variant="1/3"
				fullWidthMediaSm
				className="bg-background-raised"
			>
				<div className="bg-background-raised relative shrink-0 grow-0 basis-1/2 rounded-b-xl md:h-full md:rounded-tl-xl md:rounded-br-none">
					<div className="flex h-full flex-col items-start justify-between gap-6 p-6 md:p-12">
						<div>
							<Text variant="body" className="text-typography-weak">
								<Translate value="corgi.movement.show.heading.title" />
							</Text>

							<Text
								as="h1"
								variant="heading"
								size="x-large"
								className="mt-2 mb-6 capitalize"
								data-qa="movement-heading"
							>
								{objective}
							</Text>

							<ul className="m-0 list-inside list-disc space-y-2 p-0">
								<Text as="li" size="large">
									<Text fontWeight="bold">{petitionCountLocalized}</Text>{' '}
									<TranslatePlural
										value="corgi.movement.show.heading.total_petitions_count_label"
										count={petitionCount}
									/>
								</Text>
								<Text as="li" size="large">
									<Text fontWeight="bold">{supporterCountLocalized}</Text>{' '}
									<TranslatePlural
										value="corgi.movement.show.heading.total_supporters_count_label"
										count={supporterCount}
									/>
								</Text>
							</ul>
							<ShareGroup />
						</div>
					</div>
				</div>
			</HeroLayout>

			{description && (
				<Accordion as="section" data-qa="movement-accordion" title={translate('corgi.movement.show.about_heading')}>
					<Text as="div">{parseHtml(description)}</Text>
				</Accordion>
			)}
		</div>
	);
};
